// frontend/src/components/TutorialModal.js
import React, { useState, useEffect } from 'react';
import { tutorialData, hasSeenTutorial, markTutorialSeen } from '../utils/tutorialData';
import { CloseIcon as X, CheckCircleIcon as CheckCircle } from './Icons';

const TutorialModal = ({ pageId, onClose }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [dontShowAgain, setDontShowAgain] = useState(false);

    const tutorial = tutorialData[pageId];

    useEffect(() => {
        // Check if tutorial was already seen
        if (hasSeenTutorial(pageId)) {
            onClose?.();
            return;
        }

        // Small delay for animation
        const timer = setTimeout(() => setIsVisible(true), 100);
        return () => clearTimeout(timer);
    }, [pageId, onClose]);

    if (!tutorial) return null;

    const handleClose = () => {
        if (dontShowAgain) {
            markTutorialSeen(pageId);
        }
        setIsVisible(false);
        setTimeout(() => onClose?.(), 300);
    };

    const handleUnderstood = () => {
        markTutorialSeen(pageId);
        setIsVisible(false);
        setTimeout(() => onClose?.(), 300);
    };

    return (
        <div
            className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal */}
            <div
                className={`relative w-full max-w-lg bg-surface rounded-[2rem] shadow-elevation-3 overflow-hidden transition-all duration-500 ${isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
                    }`}
            >
                {/* Close button */}
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 w-10 h-10 rounded-full bg-surface-containerHigh flex items-center justify-center text-surface-onVariant hover:bg-surface-containerHighest hover:text-surface-on transition-colors z-10"
                >
                    <X style={{ fontSize: 20 }} />
                </button>

                {/* Header with icon */}
                <div className="bg-gradient-to-br from-primary to-primary/80 p-8 pb-12 text-center relative overflow-hidden">
                    {/* Decorative circles */}
                    <div className="absolute -top-10 -left-10 w-32 h-32 bg-white/10 rounded-full" />
                    <div className="absolute -bottom-8 -right-8 w-24 h-24 bg-white/10 rounded-full" />

                    <div className="relative">
                        <div className="w-20 h-20 mx-auto bg-white/20 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                            <span className="material-symbols-outlined text-white text-4xl">{tutorial.icon}</span>
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-1">{tutorial.title}</h2>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 -mt-6">
                    <div className="bg-surface-container rounded-2xl p-6 mb-4 shadow-elevation-1">
                        <p className="text-body-large text-surface-on leading-relaxed">
                            {tutorial.description}
                        </p>
                    </div>

                    {/* Features list */}
                    <div className="space-y-3 mb-6">
                        {tutorial.features.map((feature, index) => (
                            <div
                                key={index}
                                className="flex items-center gap-4 p-3 rounded-xl bg-surface-containerLow hover:bg-surface-containerHigh transition-colors"
                                style={{
                                    animationDelay: `${index * 100}ms`,
                                    animation: isVisible ? 'fadeInUp 0.4s ease-out forwards' : 'none'
                                }}
                            >
                                <div className="w-10 h-10 rounded-xl bg-primary-container text-primary flex items-center justify-center shrink-0">
                                    <span className="material-symbols-outlined text-xl">{feature.icon}</span>
                                </div>
                                <span className="text-body-medium text-surface-on font-medium">{feature.text}</span>
                            </div>
                        ))}
                    </div>

                    {/* Don't show again checkbox */}
                    <label className="flex items-center gap-3 mb-6 cursor-pointer group">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${dontShowAgain
                            ? 'bg-primary border-primary'
                            : 'border-outline group-hover:border-primary'
                            }`}>
                            {dontShowAgain && <CheckCircle style={{ fontSize: 14 }} className="text-primary-on" />}
                        </div>
                        <input
                            type="checkbox"
                            checked={dontShowAgain}
                            onChange={(e) => setDontShowAgain(e.target.checked)}
                            className="sr-only"
                        />
                        <span className="text-body-medium text-surface-onVariant">Nicht mehr anzeigen</span>
                    </label>

                    {/* Action button */}
                    <button
                        onClick={handleUnderstood}
                        className="w-full py-4 px-6 bg-primary text-primary-on rounded-2xl font-bold text-lg hover:bg-primary/90 active:scale-[0.98] transition-all shadow-elevation-1"
                    >
                        Verstanden!
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TutorialModal;
